import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import styled from '@emotion/styled';
import { theme } from '../../../styles/theme';

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1300;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: clamp(1rem, 3vw, 2rem);
  background: rgba(15, 23, 42, 0.82);
  backdrop-filter: blur(6px);
  cursor: zoom-out;
`;

const LightboxContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: min(1200px, 96vw);
  cursor: default;
`;

const LightboxPanel = styled.div`
  position: relative;
  width: 100%;
  max-height: 85vh;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 32px 80px rgba(0, 0, 0, 0.45);
`;

const LightboxImage = styled.img`
  display: block;
  width: 100%;
  height: auto;
  max-height: 92vh;
  object-fit: contain;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: rgba(15, 23, 42, 0.72);
  color: #fff;
  font-size: 1.5rem;
  line-height: 1;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: rgba(15, 23, 42, 0.9);
  }

  &:focus-visible {
    outline: 2px solid #fff;
    outline-offset: 2px;
  }
`;

const Caption = styled.p`
  margin: ${theme.spacing.md} 0 0;
  text-align: center;
  font-size: ${theme.typography.fontSize.sm};
  color: rgba(255, 255, 255, 0.85);
`;

export default function HeroPreviewLightbox({
  open,
  imageSrc,
  imageAlt,
  onClose,
  dialogAriaLabel = 'Full-size app preview',
}) {
  useEffect(() => {
    if (!open) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <Backdrop
      role="dialog"
      aria-modal="true"
      aria-label={dialogAriaLabel}
      onClick={onClose}
    >
      <LightboxContent onClick={(event) => event.stopPropagation()}>
        <LightboxPanel>
          <CloseButton type="button" onClick={onClose} aria-label="Close preview">
            ×
          </CloseButton>
          <LightboxImage src={imageSrc} alt={imageAlt} />
        </LightboxPanel>
        <Caption>Press Esc or click outside to close</Caption>
      </LightboxContent>
    </Backdrop>,
    document.body
  );
}
